import { Component, Input, OnInit,  } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { environment } from '@env';
import { NbDialogRef } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { IAvailableTeamUser } from '@shared/interfaces/team/available-team-user';
import { IManageTeam } from '@shared/interfaces/team/manage-team';
import { ITeam } from '@shared/interfaces/team/team';
import { AlertService } from '@shared/services/alert.service';
import { ApiService } from '@shared/services/api.service';
import { WindowService } from '@shared/services/window.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-share-device-modal',
  templateUrl: './share-device-modal.component.html',
  styleUrls: ['./share-device-modal.component.scss']
})
export class ShareDeviceModalComponent implements OnInit {

  @Input() deviceId!: string;
  loading = false;
  teams: Array<ITeam> = [];
  createNewTeam = false;
  newTeamForm!: UntypedFormGroup;
  selectedTeam: ITeam | null = null;
  selectedUserEmails: Array<string> = [];
  availableUsers: Array<IAvailableTeamUser> = [];

  private subscription = new Subscription();
  
  constructor(
    protected dialogRef: NbDialogRef<ShareDeviceModalComponent>,
    private api: ApiService,
    private fb: UntypedFormBuilder,
    private alertService: AlertService,
    private translateService: TranslateService,
    private windowService: WindowService
  ) { }

  ngOnInit() {
    this.subscription.add(
      this.api.getTeams().subscribe(res => this.teams = res.filter(({ isOwner }) => isOwner))
    );

    this.createNewTeamForm();
    this.getAvailableUsers(null);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
  
  cancel(): void {
    this.dialogRef.close();
  }

  trackByFn(index: number, item: any) {
    return item.id;
  }

  createNewTeamForm(): void {
    this.newTeamForm = this.fb.group({
      teamName: ["", Validators.compose([
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(30)
      ])]
    });
  }

  getFormControl(name: string): UntypedFormControl {
    return this.newTeamForm.get(name) as UntypedFormControl;
  }

  hasError(name: string): boolean {
    const e = this.getFormControl(name);
    return e.touched && !e.valid;
  }

  toggleInput(): void {
    this.createNewTeam = !this.createNewTeam;
    this.newTeamForm.reset();
    this.selectedTeam = null;
    this.getAvailableUsers(null);
  }

  checkDisabled(): boolean {
    if (this.createNewTeam) {
      return this.newTeamForm.invalid;
    }

    return this.selectedTeam === null || this.selectedUserEmails.length === 0;
  }

  openOrganizationTab(): void {
    this.selectedUserEmails = [];
    alert('TODO');
  }

  getAvailableUsers(teamId: string | null): void {
    this.subscription.add(
      this.api.getAvailableTeamUsers(teamId as string)
        .subscribe(res => {
          const selectedEmails = res.filter(user => user.selected).map(user => user.email);
          const allUsers = this.selectedUserEmails.concat(selectedEmails);
          
          this.availableUsers = res;
          this.selectedUserEmails = allUsers.filter((sue, index) => {
            return allUsers.indexOf(sue) === index;
          });
        })
    );
  }

  onSelectedTeamChange(team: ITeam): void {
    this.getAvailableUsers(team.id);
  }

  save(): void {
    this.loading = true;
    if (this.createNewTeam) {
      this.createTeam().then(() => this.manageTeamUsers());
    } else {
      this.manageTeamUsers();
    }
  }

  createTeam(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.newTeamForm.valid) {
        this.loading = false;
        return reject();
      }

      this.api.postCreateTeam({ name: this.newTeamForm.value.teamName })
        .subscribe({
          next: (res: ITeam) => {
            this.selectedTeam = res;
            this.alertService.success( "team.add-team-success", 5000);
            resolve();
          },
          error: () => {
            this.loading = false;
            reject();
          }
        });
    });
  }

  manageTeamUsers(): void {
    const data: IManageTeam = {
      teamId: this.selectedTeam?.id as string,
      userEmails: this.selectedUserEmails,
      deviceId: this.deviceId
    }

    this.api.postManageTeam(data)
      .subscribe({
        error: err => this.loading = false,
        next: () => {
          this.loading = false;
          this.alertService.success( this.translateService.instant("alerts.device-has-been-successfully-shared"), 5000);
          this.dialogRef.close();
        },
      });
  }
}
